import React, { ReactElement, useEffect, useMemo, useRef } from 'react';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import { useDispatch } from 'react-redux';
import {
  addAdditionalTitleBarComponents,
  removeTitleBarComponent,
  toggleTitleBarLoading,
} from '../../../../redux/pages/actions';
import {
  Select,
  TitleBarSelectValue,
} from '../../../../types/redux/pages/PagesStore';
import {
  APPEND_NEW_SELECT_TO_TITLEBAR,
  REMOVE_SELECT_FROM_TITLEBAR,
} from '../../../../types/redux/pages/pageTypes';
import useTitleBarSelect from '../../../../hooks/useTitleBarSelect';
import MancoCounterpartySummaryTable from './MancoCounterpartySummaryTable.component';
import MancoCounterpartyBaseCurrencyExposureChart from './MancoCounterpartyBaseCurrencyExposureChart.component';
import MancoExposureTable from './MancoExposureTable.component';
import { Grid, Typography } from '@mui/material';
import DisplayAreaCenteredWrapper from '../../../layout/utilities/displayAreaWrapper';
import { keepPreviousData } from '@tanstack/react-query';
import {
  useMancoCounterpartyDropdownQuery,
  useMancoCounterpartyExposurePageModel,
  type MancoCounterpartyDropdownPayload,
  type MancoCounterpartyExposurePayload,
} from '../../../../api/rs-hg/manco';
import { GeneralOverviewPageRenderedComponentProps } from '../../../layout/general/SimpleGeneralPageWrapper';

interface MancoCounterpartyExposureContainerProps {
  selectedCounterparty: string;
  selectedDate: string | undefined;
}

function buildCounterpartyOptions(
  dropdown: MancoCounterpartyDropdownPayload,
): TitleBarSelectValue[] {
  return Object.entries(dropdown)
    .filter(
      ([id, name]) =>
        !!id && id !== 'none_selected' && typeof name === 'string' && !!name,
    )
    .map(([id, name]) => ({
      label: name,
      value: id,
    }));
}

function MancoCounterpartyExposureContainer(
  props: MancoCounterpartyExposureContainerProps,
): ReactElement {
  const { selectedCounterparty, selectedDate } = props;

  const exposureQuery = useMancoCounterpartyExposurePageModel(
    selectedCounterparty,
    selectedDate,
  );

  return (
    <QueryBoundary<MancoCounterpartyExposurePayload>
      isLoading={exposureQuery.isLoading}
      isFetching={exposureQuery.isFetching}
      isError={exposureQuery.isError}
      error={exposureQuery.error}
      data={exposureQuery.data?.data}
      loadingMessages={[
        'Loading Counterparty Exposure Data...',
        'This may take a few moments...',
      ]}
      errorMessage="No positions available for this date"
    >
      {(payload) => {
        const viewData = {
          ...payload,
          selected_position_date:
            exposureQuery.data?.selected_position_date

        };

        return (
          <>
            <MancoCounterpartySummaryTable
              data={viewData}
              isFetching={exposureQuery.isFetching}
            />
            <MancoCounterpartyBaseCurrencyExposureChart
              data={viewData}
              isFetching={exposureQuery.isFetching}
            />
            <MancoExposureTable
              data={viewData}
              isFetching={exposureQuery.isFetching}
              isCounterpartyExposure
            />
          </>
        );
      }}
    </QueryBoundary>
  );
}

const MancoCounterpartyExposure: React.FC<
  GeneralOverviewPageRenderedComponentProps
> = (props) => {
  const activeDate = props.positionDate;

  const {
    data: counterpartyDropdownData,
    isLoading,
    isFetching,
    isError,
    error,
  } = useMancoCounterpartyDropdownQuery({
    placeholderData: keepPreviousData, // Keep previous options visible during refetch
  });

  const dispatch = useDispatch();
  const selectedCounterparty = useTitleBarSelect('counterpartySelection');
  const dropdownInitializedRef = useRef(false);

  const counterpartyOptions = useMemo(
    () =>
      counterpartyDropdownData
        ? buildCounterpartyOptions(counterpartyDropdownData)
        : [],
    [counterpartyDropdownData],
  );

  useEffect(() => {
    dispatch(toggleTitleBarLoading(isLoading || isFetching));
  }, [isLoading, isFetching, dispatch]);

  useEffect(() => {
    if (
      !isLoading &&
      !isFetching &&
      counterpartyOptions.length > 0 &&
      !dropdownInitializedRef.current
    ) {
      const selectsToAdd: Select[] = [
        {
          titleBarTitle: 'Select Counterparty',
          titleBarKey: 'counterpartySelection',
          values: counterpartyOptions,
          displayOnOverviewPage: true,
          aValueIsAlwaysSelected: false,
          currentValue: 'none_selected',
        },
      ];
      dispatch(
        addAdditionalTitleBarComponents(
          selectsToAdd,
          APPEND_NEW_SELECT_TO_TITLEBAR,
        ),
      );
      dispatch(toggleTitleBarLoading(false));
      dropdownInitializedRef.current = true;
    }

    return () => {
      if (dropdownInitializedRef.current) {
        dispatch(
          removeTitleBarComponent(
            'counterpartySelection',
            REMOVE_SELECT_FROM_TITLEBAR,
          ),
        );
        dropdownInitializedRef.current = false;
      }
    };
  }, [counterpartyOptions, isLoading, isFetching, dispatch]);

  const hasSelectedCounterparty =
    !!selectedCounterparty && selectedCounterparty !== 'none_selected';

  return (
    <QueryBoundary
      isLoading={isLoading}
      isFetching={isFetching}
      isError={isError}
      error={error}
      data={counterpartyOptions.length ? counterpartyOptions : null}
      loadingMessages={[
        'Loading Counterparties...',
        'This May Take Few Moments',
      ]}
      errorMessage="No positions available for this date"
    >
      {() => (
        <Grid container spacing={2} style={{ padding: 8 }}>
          {hasSelectedCounterparty ? (
            <MancoCounterpartyExposureContainer
              selectedCounterparty={selectedCounterparty}
              selectedDate={activeDate}
            />
          ) : (
            <DisplayAreaCenteredWrapper>
              <Typography variant="h2">Please Select a Counterparty</Typography>
            </DisplayAreaCenteredWrapper>
          )}
        </Grid>
      )}
    </QueryBoundary>
  );
};

export default MancoCounterpartyExposure;
